import axios from 'axios'
import { useMemo } from 'react'
import { emptyArr } from '../../constants/misc'
import { useAnalyticsFeatureFlag } from '../../contexts/AnalyticsFeatureFlagContext'
import { ChartEntry } from '../../data/model/chart'
import { SelectedFilter, WeightingScheme } from '../../data/model/results'
import { usePipingMarkerTransformer } from '../../hooks/usePipingMarkerTransformer'
import { PollStatus, usePollOn202 } from '../../hooks/usePollOn202'
import {
  getCombinedSummaryData,
  getSummaryData
} from '../../hooks/useSummaryData'
import { replaceCompletesOnWeightedData } from '../../utils/resultsUtils'

export type RehydrationStatus = 'idle' | 'rehydrating' | 'ready'

interface SummaryApiData {
  results: ChartEntry[]
  weightingScheme: WeightingScheme
  totalChartsCount: number | null
}

// Stable dependency: hash filters with actual selections so effects only re-run
// when filter VALUES change, not when the array reference changes.
const useSelectedFiltersKey = (selectedFilters: SelectedFilter[]): string =>
  useMemo(() => {
    const filtersWithSelections = selectedFilters.filter(
      (f) =>
        (f.responseOptionIds && f.responseOptionIds.length > 0) ||
        (f.branchNumbers && f.branchNumbers.length > 0)
    )
    if (filtersWithSelections.length === 0) return ''
    return filtersWithSelections
      .map((f) => {
        const responseIds = [...(f.responseOptionIds || [])].sort().join(',')
        const branchNums = [...(f.branchNumbers || [])].sort().join(',')
        return `${f.questionId || f.forkId}_${responseIds}_${branchNums}`
      })
      .sort()
      .join('|')
  }, [selectedFilters])

// Shared shaping of a summary-data poll into the chart-list view model, used by
// both the single-survey and combined (multi-market) hooks.
const useChartListResult = (
  status: PollStatus,
  data: SummaryApiData | undefined,
  languageLoading: boolean
) => {
  const filteredSummaryData = data?.results ?? emptyArr
  const { getCardTitleDisplayView } =
    usePipingMarkerTransformer(filteredSummaryData)

  const showWeightedData = !!data?.weightingScheme
  const chartData = useMemo(
    () =>
      filteredSummaryData.map((entry) => ({
        ...replaceCompletesOnWeightedData(entry, showWeightedData),
        text: getCardTitleDisplayView(entry.text),
        textStyling: getCardTitleDisplayView(entry.textStyling ?? '')
      })),
    [filteredSummaryData, showWeightedData, getCardTitleDisplayView]
  )

  const rehydrationStatus: RehydrationStatus =
    status === 'rehydrating'
      ? 'rehydrating'
      : status === 'success'
        ? 'ready'
        : 'idle'

  return {
    chartData,
    totalCount: data?.totalChartsCount ?? -1,
    // While gated, usePollOn202 sits at 'idle' — keep reporting loading so the
    // page shows its skeleton rather than an empty chart list.
    summaryDataLoading:
      languageLoading || status === 'loading' || status === 'rehydrating',
    rehydrationStatus
  }
}

// `languageLoading` holds the request until useResultsLanguages settles. Without
// it the first fetch goes out with no `language`, and the localised re-fetch that
// follows visibly replaces English question text a moment later. Note the flag
// must appear in `deps` as well as `enabled`: usePollOn202 deliberately omits
// `enabled` from its effect deps, so a change to it alone would not re-trigger.
export const useCompleteChartListData = (
  surveyId: string,
  selectedFilters: SelectedFilter[],
  page: number,
  rowsPerPage: number,
  language?: string,
  languageLoading = false
) => {
  const { useAnalyticsAPI } = useAnalyticsFeatureFlag()
  const selectedFiltersKey = useSelectedFiltersKey(selectedFilters)

  const { status, data } = usePollOn202<SummaryApiData>(
    (signal) => {
      const source = axios.CancelToken.source()
      if (signal.aborted) source.cancel()
      else
        signal.addEventListener('abort', () => source.cancel(), { once: true })
      return getSummaryData(
        surveyId,
        page * rowsPerPage,
        rowsPerPage,
        selectedFilters,
        source.token,
        useAnalyticsAPI,
        language
      )
    },
    [
      page,
      rowsPerPage,
      selectedFiltersKey,
      surveyId,
      useAnalyticsAPI,
      language,
      languageLoading
    ],
    { enabled: useAnalyticsAPI !== null && !languageLoading }
  )

  return useChartListResult(status, data, languageLoading)
}

// Combined (multi-market) chart list: keyed on the project rather than a single
// survey and always uses the core-responses analytics API (no feature-flag
// gate, since there is no legacy combined endpoint). usePollOn202 is reused
// purely as the fetch/cancellation runner — the combined endpoint never 202s
// (its surveys are always freshly ingested), so it never actually polls.
export const useCombinedChartListData = (
  projectId: string,
  selectedFilters: SelectedFilter[],
  page: number,
  rowsPerPage: number,
  language?: string,
  languageLoading = false
) => {
  const selectedFiltersKey = useSelectedFiltersKey(selectedFilters)

  const { status, data } = usePollOn202<SummaryApiData>(
    (signal) => {
      const source = axios.CancelToken.source()
      if (signal.aborted) source.cancel()
      else
        signal.addEventListener('abort', () => source.cancel(), { once: true })
      return getCombinedSummaryData(
        projectId,
        page * rowsPerPage,
        rowsPerPage,
        selectedFilters,
        source.token,
        language
      )
    },
    [
      page,
      rowsPerPage,
      selectedFiltersKey,
      projectId,
      language,
      languageLoading
    ],
    { enabled: !languageLoading }
  )

  return useChartListResult(status, data, languageLoading)
}
